import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import FileIcon from "../../assets/image/file.svg";
import { mapViewToForm } from "./driverViewMap";
import SingleDatePicker from "../../Components/SingleDatePicker/SingleDatePicker";
import CalendarIcon from "../../assets/image/calendar-icon.svg";
import { useBackToList } from "../../hooks/useBackToList";
import { canAccountantEditDriverLicense } from "../../utils/driverLicenseEditEligibility";

const ACCOUNTANT_API = process.env.REACT_APP_ACCOUNTANT_API;

const LICENSE_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/jpg";
const LICENSE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

const initialErrors = {
  license_expiry: "",
  nt_license_expiry: "",
  license_image: "",
};

const validateLicenseImageFile = (file) => {
  if (!file.type.startsWith("image/")) {
    return "Please choose a JPEG, PNG, or WebP image";
  }
  if (file.size > LICENSE_IMAGE_MAX_BYTES) {
    return "Image must be 5 MB or smaller";
  }
  return "";
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response?.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response?.status || ""})`
  );
};

const pickDriverPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const data = payload.data;
  if (Array.isArray(data) && data[0]) return data[0];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (data.driver) return data.driver;
    if (data.driver_data && !Array.isArray(data.driver_data)) return data.driver_data;
    if (Array.isArray(data.driver_data) && data.driver_data[0]) return data.driver_data[0];
    return data;
  }
  if (payload.driver) return payload.driver;
  return null;
};

const documentTypeLabel = (documentType) => {
  const k = String(documentType ?? "").toLowerCase();
  if (k === "driver_license") return "Driver license (front)";
  if (k === "driver_license_back") return "Driver license (back)";
  if (k.includes("license") || k.includes("licence")) return "Driving license";
  if (k.includes("adhar_card_back") || k.includes("aadhar")) return "Aadhaar card back";
  if (k.includes("adhar_card_front") || k.includes("aadhar")) return "Aadhaar card front";
  if (!k) return "Document";
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const isLicenseDocument = (doc) => {
  const k = String(doc?.document_type ?? "").toLowerCase();
  return k.includes("license") || k.includes("licence");
};

const findDocumentByType = (docs, type) =>
  (Array.isArray(docs) ? docs : []).find(
    (doc) => String(doc?.document_type ?? "").toLowerCase() === type
  );

const findLicenseFrontDocument = (docs) => {
  const front = findDocumentByType(docs, "driver_license");
  if (front) return front;
  return (Array.isArray(docs) ? docs : []).find((doc) => {
    const k = String(doc?.document_type ?? "").toLowerCase();
    return (k.includes("license") || k.includes("licence")) && !k.includes("back");
  });
};

const findLicenseBackDocument = (docs) => findDocumentByType(docs, "driver_license_back");

const LicenseImageThumb = ({ previewUrl, imageError, onImageError, label = "Driving license" }) => (
  <div className="flex w-48 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
    <div className="relative box-border flex h-36 w-full min-h-0 shrink-0 items-center justify-center overflow-hidden bg-gray-100 p-2">
      {previewUrl && !imageError ? (
        <img
          src={previewUrl}
          alt="Driving license"
          className="box-border max-h-full max-w-full object-contain"
          loading="lazy"
          decoding="async"
          onError={onImageError}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2">
          <img src={FileIcon} className="w-8 opacity-60" alt="" />
          <span className="text-xs text-gray-500">No image</span>
        </div>
      )}
    </div>
    <span className="border-t border-gray-100 px-2 py-2 text-center text-sm font-UbuntuMedium text-gray-700">
      {label}
    </span>
  </div>
);

const LicenseUploadModal = ({
  open,
  frontPreviewUrl,
  backPreviewUrl,
  frontImageError,
  backImageError,
  onFrontImageError,
  onBackImageError,
  onPickFront,
  onPickBack,
  onContinue,
  onCancel,
  errorMessage,
  disabled,
  reasonLabel,
}) => {
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  if (!open) return null;

  const canContinue = Boolean(frontPreviewUrl && backPreviewUrl);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 font-UbuntuRegular"
      role="presentation"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white px-6 pb-8 pt-7 shadow-xl sm:px-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="license-upload-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h4
          id="license-upload-modal-title"
          className="mb-3 text-center text-xl font-UbuntuBold text-gray-900"
        >
          Upload new driving license
        </h4>
        <p className="mb-5 text-center text-sm leading-relaxed text-gray-600">
          {reasonLabel ||
            "You changed a license expiry date. Please upload updated driver license front and back images to continue."}
        </p>

        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-center text-sm font-UbuntuMedium text-gray-800">
              Driver license (front)
            </p>
            <div className="mx-auto flex h-40 w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100 p-2">
              {frontPreviewUrl && !frontImageError ? (
                <img
                  src={frontPreviewUrl}
                  alt="License front preview"
                  className="max-h-full max-w-full object-contain"
                  onError={onFrontImageError}
                />
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <img src={FileIcon} className="w-10 opacity-60" alt="" />
                  <span className="text-xs text-gray-500">No preview</span>
                </div>
              )}
            </div>
            <input
              ref={frontInputRef}
              type="file"
              accept={LICENSE_IMAGE_ACCEPT}
              className="hidden"
              disabled={disabled}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onPickFront(file);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="mt-3 w-full rounded-xl border border-red-600 py-2.5 text-center text-sm font-UbuntuBold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              disabled={disabled}
              onClick={() => frontInputRef.current?.click()}
            >
              Choose front image
            </button>
          </div>

          <div>
            <p className="mb-2 text-center text-sm font-UbuntuMedium text-gray-800">
              Driver license (back)
            </p>
            <div className="mx-auto flex h-40 w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100 p-2">
              {backPreviewUrl && !backImageError ? (
                <img
                  src={backPreviewUrl}
                  alt="License back preview"
                  className="max-h-full max-w-full object-contain"
                  onError={onBackImageError}
                />
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <img src={FileIcon} className="w-10 opacity-60" alt="" />
                  <span className="text-xs text-gray-500">No preview</span>
                </div>
              )}
            </div>
            <input
              ref={backInputRef}
              type="file"
              accept={LICENSE_IMAGE_ACCEPT}
              className="hidden"
              disabled={disabled}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onPickBack(file);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="mt-3 w-full rounded-xl border border-red-600 py-2.5 text-center text-sm font-UbuntuBold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              disabled={disabled}
              onClick={() => backInputRef.current?.click()}
            >
              Choose back image
            </button>
          </div>
        </div>

        {errorMessage ? (
          <p className="mb-4 text-center text-sm text-red-500">{errorMessage}</p>
        ) : null}

        <div className="flex flex-col gap-3">
          <button
            type="button"
            className="w-full rounded-xl bg-red-600 py-3.5 text-center text-base font-UbuntuBold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled || !canContinue}
            onClick={onContinue}
          >
            Continue
          </button>
          <button
            type="button"
            className="w-full rounded-xl py-3.5 text-center text-base font-UbuntuBold text-white shadow-sm transition-opacity hover:opacity-95 disabled:opacity-50"
            style={{ backgroundColor: "#030253" }}
            disabled={disabled}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
        <p className="mb-0 mt-4 text-center text-xs text-gray-500">
          JPEG, PNG, or WebP · Max 5 MB · Both front and back images are required
        </p>
      </div>
    </div>
  );
};

const EditDocumentThumb = ({ doc }) => {
  const [imgError, setImgError] = useState(false);
  const url = typeof doc?.document_image === "string" ? doc.document_image.trim() : "";
  const label = documentTypeLabel(doc?.document_type);

  return (
    <div className="flex w-48 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="relative box-border flex h-36 w-full min-h-0 shrink-0 items-center justify-center overflow-hidden bg-gray-100 p-2">
        {url && !imgError ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-full max-h-full w-full max-w-full min-h-0 min-w-0 items-center justify-center rounded-md"
            aria-label={`Open ${label} in new tab`}
          >
            <img
              src={url}
              alt={label}
              className="box-border max-h-full max-w-full object-contain"
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
            />
          </a>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2">
            <img src={FileIcon} className="w-8 opacity-60" alt="" />
            <span className="text-xs text-gray-500">Preview unavailable</span>
          </div>
        )}
      </div>
      <span className="border-t border-gray-100 px-2 py-2 text-center text-sm font-UbuntuMedium text-gray-700">
        {label}
      </span>
    </div>
  );
};

const EditDriverDetails = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const driverId = (searchParams.get("driver_id") || "").trim();
  const backToList = useBackToList("/driver-management");

  const [loadingDriver, setLoadingDriver] = useState(() => Boolean(driverId));
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(initialErrors);

  const [fullName, setFullName] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseCreation, setLicenseCreation] = useState("");
  const [licenseExpiry, setLicenseExpiry] = useState("");
  const [baselineLicenseExpiry, setBaselineLicenseExpiry] = useState("");
  const [ntLicenseExpiry, setNtLicenseExpiry] = useState("");
  const [baselineNtLicenseExpiry, setBaselineNtLicenseExpiry] = useState("");
  const [adharNumber, setAdharNumber] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [age, setAge] = useState("");
  const [accountantName, setAccountantName] = useState("");
  const [miplLocation, setMiplLocation] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const [documentData, setDocumentData] = useState([]);

  const [licenseImageUrl, setLicenseImageUrl] = useState("");
  const [licenseImagePreview, setLicenseImagePreview] = useState("");
  const [licenseImageFile, setLicenseImageFile] = useState(null);
  const [licenseImageBroken, setLicenseImageBroken] = useState(false);
  const [licenseBackImageUrl, setLicenseBackImageUrl] = useState("");
  const [licenseBackImagePreview, setLicenseBackImagePreview] = useState("");
  const [licenseBackImageFile, setLicenseBackImageFile] = useState(null);
  const [licenseBackImageBroken, setLicenseBackImageBroken] = useState(false);
  const [showLicenseUploadModal, setShowLicenseUploadModal] = useState(false);
  const licensePreviewObjectUrlRef = useRef(null);
  const licenseBackPreviewObjectUrlRef = useRef(null);

  useEffect(() => {
    setSubheading("Edit Driver Details");
    return () => setSubheading("");
  }, [setSubheading]);

  useEffect(() => {
    return () => {
      if (licensePreviewObjectUrlRef.current) {
        URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
      }
      if (licenseBackPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!driverId) {
      setLoadingDriver(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setLoadingDriver(false);
        return;
      }

      setLoadingDriver(true);
      try {
        const response = await fetch(
          `${ACCOUNTANT_API}${endpoint.viewDriver}?driver_id=${encodeURIComponent(driverId)}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
          toast.error(getErrorMessage(payload, response));
          return;
        }

        const raw = pickDriverPayload(payload);
        const form = mapViewToForm(raw);
        if (!form) {
          toast.error("Could not load driver details.");
          return;
        }

        if (!canAccountantEditDriverLicense(raw)) {
          toast.error(
            "Edit is only allowed when TR or NT license expires today, tomorrow, or is already expired."
          );
          navigate(backToList, { replace: true });
          return;
        }

        setFullName(form.fullName);
        setLicenseNumber(form.licenseNumber);
        setLicenseCreation(form.licenseCreation);
        setLicenseExpiry(form.licenseExpiry);
        setBaselineLicenseExpiry(form.licenseExpiry || "");
        setNtLicenseExpiry(form.ntLicenseExpiry);
        setBaselineNtLicenseExpiry(form.ntLicenseExpiry || "");
        setAdharNumber(form.adharNumber);
        setMobileNumber(form.mobile);
        setAge(form.age);
        setAccountantName(form.accountantName);
        setMiplLocation(form.miplLocation);
        setProfileImage(form.profileImage);
        setProfileImageBroken(false);

        const docs = form.documentData;
        setDocumentData(docs);
        const licenseDoc = findLicenseFrontDocument(docs);
        const licenseBackDoc = findLicenseBackDocument(docs);
        const licenseUrl =
          typeof licenseDoc?.document_image === "string"
            ? licenseDoc.document_image.trim()
            : "";
        const licenseBackUrl =
          typeof licenseBackDoc?.document_image === "string"
            ? licenseBackDoc.document_image.trim()
            : "";
        setLicenseImageUrl(licenseUrl);
        setLicenseImagePreview(licenseUrl);
        setLicenseBackImageUrl(licenseBackUrl);
        setLicenseBackImagePreview(licenseBackUrl);
        setLicenseBackImageBroken(false);
        setLicenseImageFile(null);
        setLicenseBackImageFile(null);
        setLicenseImageBroken(false);
        if (licensePreviewObjectUrlRef.current) {
          URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
          licensePreviewObjectUrlRef.current = null;
        }
        if (licenseBackPreviewObjectUrlRef.current) {
          URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
          licenseBackPreviewObjectUrlRef.current = null;
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load driver details."
          );
        }
      } finally {
        if (!cancelled) setLoadingDriver(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [driverId]);

  const clearFieldError = (key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const clearUploadedLicenseFiles = useCallback(() => {
    if (licensePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
      licensePreviewObjectUrlRef.current = null;
    }
    if (licenseBackPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
      licenseBackPreviewObjectUrlRef.current = null;
    }
    setLicenseImageFile(null);
    setLicenseBackImageFile(null);
    setLicenseImagePreview(licenseImageUrl);
    setLicenseBackImagePreview(licenseBackImageUrl);
    setLicenseImageBroken(false);
    setLicenseBackImageBroken(false);
  }, [licenseImageUrl, licenseBackImageUrl]);

  const getLicenseUploadReason = useCallback(
    (trValue, ntValue) => {
      const trChanged = (trValue || "").trim() !== (baselineLicenseExpiry || "").trim();
      const ntChanged = (ntValue || "").trim() !== (baselineNtLicenseExpiry || "").trim();
      if (trChanged && ntChanged) {
        return "You changed the TR and NT license expiry dates. Please upload updated driver license front and back images to continue.";
      }
      if (trChanged) {
        return "You changed the TR license expiry date. Please upload updated driver license front and back images to continue.";
      }
      if (ntChanged) {
        return "You changed the NT license expiry date. Please upload updated driver license front and back images to continue.";
      }
      return "";
    },
    [baselineLicenseExpiry, baselineNtLicenseExpiry]
  );

  const openLicenseUploadModal = useCallback(() => {
    setShowLicenseUploadModal(true);
  }, []);

  const handleLicenseImagePick = (file) => {
    const validationError = validateLicenseImageFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, license_image: validationError }));
      return;
    }
    if (licensePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licensePreviewObjectUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(file);
    licensePreviewObjectUrlRef.current = objectUrl;
    setLicenseImageFile(file);
    setLicenseImagePreview(objectUrl);
    setLicenseImageBroken(false);
    clearFieldError("license_image");
  };

  const handleLicenseBackImagePick = (file) => {
    const validationError = validateLicenseImageFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, license_image: validationError }));
      return;
    }
    if (licenseBackPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(licenseBackPreviewObjectUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(file);
    licenseBackPreviewObjectUrlRef.current = objectUrl;
    setLicenseBackImageFile(file);
    setLicenseBackImagePreview(objectUrl);
    setLicenseBackImageBroken(false);
    clearFieldError("license_image");
  };

  const closeLicenseUploadModal = useCallback(() => {
    setShowLicenseUploadModal(false);
    setLicenseExpiry(baselineLicenseExpiry);
    setNtLicenseExpiry(baselineNtLicenseExpiry);
    clearUploadedLicenseFiles();
    clearFieldError("license_image");
  }, [baselineLicenseExpiry, baselineNtLicenseExpiry, clearUploadedLicenseFiles]);

  const continueLicenseUploadModal = useCallback(() => {
    if (!licenseImageFile || !licenseBackImageFile) {
      setErrors((prev) => ({
        ...prev,
        license_image: "Upload both driver license front and back images to continue",
      }));
      return;
    }
    setShowLicenseUploadModal(false);
    clearFieldError("license_image");
  }, [licenseImageFile, licenseBackImageFile]);

  const handleLicenseExpiryChange = (nextExpiry) => {
    const next = (nextExpiry || "").trim();
    const baseline = (baselineLicenseExpiry || "").trim();

    clearFieldError("license_expiry");
    setLicenseExpiry(nextExpiry);

    if (baseline && next && next !== baseline) {
      openLicenseUploadModal();
      return;
    }

    if (
      (nextExpiry || "").trim() === baseline &&
      (ntLicenseExpiry || "").trim() === (baselineNtLicenseExpiry || "").trim()
    ) {
      setShowLicenseUploadModal(false);
      clearUploadedLicenseFiles();
      clearFieldError("license_image");
    }
  };

  const handleNtLicenseExpiryChange = (nextExpiry) => {
    const next = (nextExpiry || "").trim();
    const baseline = (baselineNtLicenseExpiry || "").trim();

    clearFieldError("nt_license_expiry");
    setNtLicenseExpiry(nextExpiry);

    if (baseline && next && next !== baseline) {
      openLicenseUploadModal();
      return;
    }

    if (
      (nextExpiry || "").trim() === baseline &&
      (licenseExpiry || "").trim() === (baselineLicenseExpiry || "").trim()
    ) {
      setShowLicenseUploadModal(false);
      clearUploadedLicenseFiles();
      clearFieldError("license_image");
    }
  };

  const validate = () => {
    const next = { ...initialErrors };
    const expiry = (licenseExpiry || "").trim();
    const ntExpiry = (ntLicenseExpiry || "").trim();
    const trChanged = expiry !== (baselineLicenseExpiry || "").trim();
    const ntChanged = ntExpiry !== (baselineNtLicenseExpiry || "").trim();
    const licenseDocsRequired = trChanged || ntChanged;

    if (!expiry) next.license_expiry = "TR license expiry date is required";
    else if (licenseCreation && expiry < licenseCreation) {
      next.license_expiry = "TR expiry date must be on or after creation date";
    }

    if (!ntExpiry) next.nt_license_expiry = "NT license expiry date is required";
    else if (licenseCreation && ntExpiry < licenseCreation) {
      next.nt_license_expiry = "NT expiry date must be on or after creation date";
    }

    if (licenseDocsRequired && (!licenseImageFile || !licenseBackImageFile)) {
      next.license_image =
        "Upload both driver license front and back images to change the expiry date";
      openLicenseUploadModal();
    }

    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const buildEditFormData = () => {
    const formData = new FormData();
    formData.append("driver_id", driverId);
    formData.append("license_expiry_date", (licenseExpiry || "").trim());
    formData.append("nt_license_expiry_date", (ntLicenseExpiry || "").trim());
    if (licenseImageFile) {
      formData.append("license_doc", licenseImageFile, licenseImageFile.name);
    }
    if (licenseBackImageFile) {
      formData.append("license_doc_back", licenseBackImageFile, licenseBackImageFile.name);
    }
    return formData;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!driverId) {
      toast.error("Driver ID is missing");
      return;
    }
    if (!validate()) return;

    const expiry = (licenseExpiry || "").trim();
    const ntExpiry = (ntLicenseExpiry || "").trim();
    const trChanged = expiry !== (baselineLicenseExpiry || "").trim();
    const ntChanged = ntExpiry !== (baselineNtLicenseExpiry || "").trim();
    if (!trChanged && !ntChanged) {
      toast.info("No changes to save.");
      return;
    }

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${ACCOUNTANT_API}${endpoint.editDriver}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: buildEditFormData(),
      });
      const payload = await parseBody(response);

      if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      toast.success(payload?.message || "Driver updated successfully");
      setTimeout(() => navigate(backToList, { replace: true }), 1200);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!showLicenseUploadModal) return undefined;
    const onKey = (ev) => {
      if (ev.key === "Escape") closeLicenseUploadModal();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [showLicenseUploadModal, closeLicenseUploadModal]);

  const disabledForm = submitting || loadingDriver || !driverId || showLicenseUploadModal;

  const otherDocuments = useMemo(
    () => documentData.filter((doc) => !isLicenseDocument(doc)),
    [documentData]
  );

  const licensePreviewSrc = licenseImagePreview || licenseImageUrl;
  const licenseBackPreviewSrc = licenseBackImagePreview || licenseBackImageUrl;
  const licenseUploadReason = getLicenseUploadReason(licenseExpiry, ntLicenseExpiry);
  const mobileDisplay = mobileNumber
    ? `${"+91"} ${mobileNumber}`.trim()
    : "—";

  if (!driverId && !loadingDriver) {
    return (
      <>
        <Toastify />
        <div className="mx-auto mt-7 max-w-5xl px-4 font-UbuntuRegular">
          <p className="text-gray-600">Missing driver id. Open edit from the driver list.</p>
          <Link to={backToList} className="btn btn-primary mt-3">
            Back to list
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="col-md-4">
        <Link to={backToList} className="btn btn-primary shrink-0 mt-4">
          Back
        </Link>
      </div>
      <Toastify />
      <LicenseUploadModal
        open={showLicenseUploadModal}
        frontPreviewUrl={licenseImageFile ? licensePreviewSrc : ""}
        backPreviewUrl={licenseBackImageFile ? licenseBackPreviewSrc : ""}
        frontImageError={licenseImageBroken}
        backImageError={licenseBackImageBroken}
        onFrontImageError={() => setLicenseImageBroken(true)}
        onBackImageError={() => setLicenseBackImageBroken(true)}
        onPickFront={handleLicenseImagePick}
        onPickBack={handleLicenseBackImagePick}
        onContinue={continueLicenseUploadModal}
        onCancel={closeLicenseUploadModal}
        errorMessage={errors.license_image}
        disabled={submitting || loadingDriver}
        reasonLabel={licenseUploadReason}
      />
      <form onSubmit={onSubmit} noValidate>
        <div className="mt-7 text-center">
          <div className="mx-auto inline-flex h-40 w-40 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100">
            {!loadingDriver && profileImage && !profileImageBroken ? (
              <img
                src={profileImage}
                alt={fullName ? `${fullName} profile` : "Driver profile"}
                className="h-full w-full object-cover"
                onError={() => setProfileImageBroken(true)}
              />
            ) : !loadingDriver ? (
              <span className="text-4xl font-UbuntuMedium text-gray-400">
                {(fullName || "?").trim().slice(0, 1).toUpperCase() || "?"}
              </span>
            ) : null}
          </div>
          {!loadingDriver && fullName ? (
            <p className="mt-3 mb-0 text-lg font-UbuntuMedium text-gray-900">{fullName}</p>
          ) : null}
        </div>
        <div className="mx-auto max-w-5xl px-4">
          {loadingDriver ? <p className="my-4 text-gray-500">Loading driver…</p> : null}
          <div className="my-4 grid grid-cols-1 gap-3 font-UbuntuRegular sm:grid-cols-2 lg:grid-cols-3 [&_.form-control]:min-h-11 [&_.form-control:disabled]:bg-slate-200">
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Name</label>
              <input
                type="text"
                className="form-control"
                value={fullName}
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Driver license number</label>
              <input
                type="text"
                className="form-control"
                value={licenseNumber}
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <SingleDatePicker
                variant="field"
                label="License creation date"
                value={licenseCreation}
                onChange={() => {}}
                disabled
                filterIcon={CalendarIcon}
              />
            </div>
            <div className="form-group">
              <SingleDatePicker
                variant="field"
                label="TR License Expiry Date"
                value={licenseExpiry}
                minDate={licenseCreation || undefined}
                onChange={handleLicenseExpiryChange}
                disabled={disabledForm}
                filterIcon={CalendarIcon}
                className={errors.license_expiry ? "[&_button]:border-red-500" : ""}
              />
              {errors.license_expiry ? (
                <p className="mt-1 mb-0 text-sm text-red-500">{errors.license_expiry}</p>
              ) : null}
            </div>
            <div className="form-group">
              <SingleDatePicker
                variant="field"
                label="NT License Expiry Date"
                value={ntLicenseExpiry}
                minDate={licenseCreation || undefined}
                onChange={handleNtLicenseExpiryChange}
                disabled={disabledForm}
                filterIcon={CalendarIcon}
                className={errors.nt_license_expiry ? "[&_button]:border-red-500" : ""}
              />
              {errors.nt_license_expiry ? (
                <p className="mt-1 mb-0 text-sm text-red-500">{errors.nt_license_expiry}</p>
              ) : null}
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Mobile number</label>
              <input type="text" className="form-control" value={mobileDisplay} readOnly disabled />
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Age</label>
              <input type="text" className="form-control" value={age || "—"} readOnly disabled />
            </div>
            <div className="form-group">
              <label className="mb-1 block font-UbuntuMedium text-sm">Aadhaar card number</label>
              <input type="text" className="form-control" value={adharNumber || "—"} readOnly disabled />
            </div>
            {accountantName ? (
              <div className="form-group">
                <label className="mb-1 block font-UbuntuMedium text-sm">Accountant name</label>
                <input type="text" className="form-control" value={accountantName} readOnly disabled />
              </div>
            ) : null}
            {miplLocation ? (
              <div className="form-group">
                <label className="mb-1 block font-UbuntuMedium text-sm">MIPL location</label>
                <input type="text" className="form-control" value={miplLocation} readOnly disabled />
              </div>
            ) : null}
          </div>

          <h3 className="mb-2 text-xl font-UbuntuMedium">ID proof documents</h3>
          <div className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-3">
            <LicenseImageThumb
              previewUrl={licensePreviewSrc}
              imageError={licenseImageBroken}
              onImageError={() => setLicenseImageBroken(true)}
              label="Driver license (front)"
            />
            <LicenseImageThumb
              previewUrl={licenseBackPreviewSrc}
              imageError={licenseBackImageBroken}
              onImageError={() => setLicenseBackImageBroken(true)}
              label="Driver license (back)"
            />
            {errors.license_image ? (
              <p className="mb-0 w-full text-sm text-red-500">{errors.license_image}</p>
            ) : null}
            {otherDocuments.map((doc, idx) => (
              <EditDocumentThumb key={`${doc?.document_type || "doc"}-${idx}`} doc={doc} />
            ))}
            {otherDocuments.length === 0 && !licensePreviewSrc && !licenseBackPreviewSrc ? (
              <p className="text-sm text-gray-500">No other document images on file.</p>
            ) : null}
          </div>

          <div className="col-md-12 my-2 flex flex-wrap items-center gap-3 font-UbuntuRegular">
            <Link
              to={backToList}
              className="btn btn-primary"
              onClick={(e) => submitting && e.preventDefault()}
            >
              Cancel
            </Link>
            <button type="submit" className="btn btn-danger md:min-w-32" disabled={disabledForm}>
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditDriverDetails;
